import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { 
  CheckCircle, 
  Clock, 
  FileText, 
  Shield, 
  ArrowRight,
  Mail,
  Smartphone,
  Globe,
  ScrollText,
  Pen
} from 'lucide-react';
import { pmaContract } from '../data/pmaContract';

const ApplicationSuccess = ({ applicationData, onContinue }) => {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [contractSigned, setContractSigned] = useState(false);
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleContractSigning = async () => {
    setSigningInProgress(true);
    
    try {
      // Call backend to process contract signing
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/membership/contract-signed/${applicationData?.applicationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to process contract signing');
      }
      
      const result = await response.json();
      
      // Store signature information
      localStorage.setItem('pmaContractSigned', JSON.stringify({
        applicationId: applicationData?.applicationId,
        signedAt: new Date().toISOString(),
        memberName: applicationData?.memberName || 'Member',
        membershipLevel: applicationData?.membershipLevel
      }));
      
      setContractSigned(true);
      
      // If Basic membership, no payment needed - show Basic member portal
      if (applicationData?.membershipLevel === 'Basic') {
        // Store application data for the Basic member portal
        localStorage.setItem('basicMemberApplication', JSON.stringify({
          applicationId: applicationData?.applicationId,
          membershipLevel: applicationData?.membershipLevel,
          memberName: applicationData?.memberName || 'Basic Member',
          email: applicationData?.email,
          activatedAt: new Date().toISOString()
        }));
        
        setTimeout(() => {
          // Redirect to Basic member portal on the same site
          window.location.href = '/basic-portal';
        }, 2000);
      } else {
        // For paid memberships, initiate payment flow
        setTimeout(() => {
          handlePaymentInitiation();
        }, 2000);
      }
      
    } catch (error) {
      console.error('Contract signing error:', error);
      alert('Failed to process contract signing. Please try again.');
    } finally {
      setSigningInProgress(false);
    }
  };

  const handlePaymentInitiation = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/membership/initiate-payment/${applicationData?.applicationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          billing_frequency: 'monthly' // Default to monthly, could be made configurable
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to initiate payment');
      }
      
      const result = await response.json();
      
      if (result.success && result.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkout_url;
      } else {
        throw new Error('Invalid payment response');
      }
      
    } catch (error) {
      console.error('Payment initiation error:', error);
      alert('Failed to initiate payment. Please contact support.');
    }
  };

  const getMembershipBadgeColor = (level) => {
    switch(level) {
      case 'Basic': return 'bg-green-100 text-green-800 border-green-300';
      case 'Standard': return 'bg-amber-100 text-amber-800 border-amber-300';  
      case 'Premium': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        
        {/* Success Header */}
        <div className="text-center mb-8">
          {!contractSigned ? (
            // Show "Almost Finished" message before contract signing
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="text-4xl font-bold text-amber-600 mb-4">
                📋 One Final Step!
              </h1>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-4">
                Your {applicationData?.membershipLevel || 'membership'} application has been received. 
                Please scroll down to confirm your digital signature and activate your membership.
              </p>
              <div className="flex items-center justify-center space-x-2 text-amber-600">
                <span className="text-lg font-medium">Scroll Down to Sign</span>
                <div className="text-2xl">⬇️</div>
              </div>
            </>
          ) : (
            // Show success message after contract is signed
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-4xl font-bold text-green-600 mb-4">
                🎉 Application Complete!
              </h1>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Welcome to Legacy Property Association! Your {applicationData?.membershipLevel || 'membership'} membership 
                is now active and ready to use.
              </p>
            </>
          )}
        </div>

        {/* Application Details */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          
          {/* Application Summary */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-amber-50">
              <CardTitle className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <span>Application Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Application ID:</span>
                <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                  {applicationData?.applicationId || 'Loading...'}
                </code>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Membership Level:</span>
                <Badge className={getMembershipBadgeColor(applicationData?.membershipLevel)}>
                  {applicationData?.membershipLevel || 'Standard'}
                </Badge>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Processing Time:</span>
                <span className="font-medium text-green-600">
                  {formatTime(timeElapsed)} (Auto-Approved)
                </span>
              </div>

              {applicationData?.portalUsername && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Portal Username:</span>
                  <code className="bg-blue-50 px-2 py-1 rounded text-sm text-blue-700">
                    {applicationData.portalUsername}
                  </code>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-blue-50">
              <CardTitle className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <span>Next Steps</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {(applicationData?.nextSteps || [
                  "Confirm your digital signature below",
                  "Membership activates immediately upon confirmation",
                  applicationData?.membershipLevel === 'Basic' 
                    ? "Access your member portal instantly"
                    : "Complete payment setup to access full portal"
                ]).map((step, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-blue-600">{index + 1}</span>
                    </div>
                    <span className="text-slate-700 text-sm">{step}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Digital Signature Confirmation Section */}
        <Card className={`shadow-lg mb-8 ${!contractSigned ? 'ring-2 ring-amber-400 ring-opacity-75' : ''}`}>
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardTitle className="flex items-center space-x-2">
              <Pen className="w-5 h-5" />
              <span>Digital Signature Confirmation</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {!contractSigned ? (
              <div>
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-sm leading-relaxed">
                    <Shield className="w-5 h-5 inline mr-2" />
                    You have already reviewed and agreed to the PMA terms on the application page. 
                    Please provide your digital signature below to confirm your membership and activate your account.
                  </p>
                </div>

                {/* Simplified Signature Confirmation */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-6 mb-6 border-2 border-blue-100">
                  <div className="text-center mb-6">
                    <ScrollText className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      Legacy Property Council PMA Agreement
                    </h3>
                    <p className="text-sm text-slate-600">State of Illinois</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 mb-4 border border-slate-200">
                    <p className="text-sm text-slate-700 leading-relaxed text-center">
                      By signing below, you confirm that you have read, understood, and agree to the 
                      Private Member Association Agreement terms that were presented during your application.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div className="flex flex-col items-center p-3 bg-blue-50 rounded-lg">
                      <CheckCircle className="w-6 h-6 text-blue-600 mb-2" />
                      <p className="text-xs font-semibold text-slate-700">Terms Agreed</p>
                      <p className="text-xs text-slate-500">On application page</p>
                    </div>
                    <div className="flex flex-col items-center p-3 bg-amber-50 rounded-lg">
                      <Pen className="w-6 h-6 text-amber-600 mb-2" />
                      <p className="text-xs font-semibold text-slate-700">Digital Signature</p>
                      <p className="text-xs text-slate-500">Confirm below</p>
                    </div>
                    <div className="flex flex-col items-center p-3 bg-green-50 rounded-lg">
                      <Shield className="w-6 h-6 text-green-600 mb-2" />
                      <p className="text-xs font-semibold text-slate-700">Membership Active</p>
                      <p className="text-xs text-slate-500">After signature</p>
                    </div>
                  </div>
                </div>

                {/* Digital Signature Section */}
                <div className="border-t pt-6">
                  <div className="flex items-start space-x-3 mb-4">
                    <Checkbox 
                      id="contract-agreement"
                      checked={agreementChecked}
                      onCheckedChange={setAgreementChecked}
                      className="mt-1"
                    />
                    <label htmlFor="contract-agreement" className="text-sm text-slate-700 leading-relaxed">
                      I confirm that I have read and agreed to the PMA Agreement terms during my application. 
                      I consent to electronic signature and digital record keeping. This is my official digital signature.
                    </label>
                  </div>
                  
                  <Button 
                    onClick={handleContractSigning}
                    disabled={signingInProgress || !agreementChecked}
                    className={`w-full py-4 text-lg transition-all duration-300 ${
                      agreementChecked && !signingInProgress
                        ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                        : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    {signingInProgress ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing Digital Signature...
                      </>
                    ) : (
                      <>
                        <Pen className="w-5 h-5 mr-2" />
                        Confirm Digital Signature
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-2">Contract Successfully Signed!</h3>
                {applicationData?.membershipLevel === 'Basic' ? (
                  <p className="text-green-700 mb-4">
                    Your PMA Agreement has been digitally signed and your Basic membership is now active! 
                    A copy has been sent to your email. You'll be redirected to your member portal to access 
                    educational resources, legal documents, and property forms.
                  </p>
                ) : (
                  <p className="text-green-700 mb-4">
                    Your PMA Agreement has been digitally signed and a copy has been sent to your email. 
                    You'll now be redirected to complete your membership payment setup.
                  </p>
                )}
                <div className="grid md:grid-cols-3 gap-6 text-center mt-6">
                  <div className="flex flex-col items-center space-y-2">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <h4 className="font-semibold text-slate-800">Membership Active</h4>
                    <p className="text-sm text-slate-600">
                      Your {applicationData?.membershipLevel || 'membership'} benefits are now available
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center space-y-2">
                    <Globe className="w-8 h-8 text-blue-600" />
                    <h4 className="font-semibold text-slate-800">Portal Access</h4>
                    <p className="text-sm text-slate-600">
                      {applicationData?.membershipLevel === 'Basic' 
                        ? 'Educational resources & documents now available'
                        : 'Member portal access activated'}
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-center space-y-2">
                    <Shield className="w-8 h-8 text-amber-600" />
                    <h4 className="font-semibold text-slate-800">Privacy Protected</h4>
                    <p className="text-sm text-slate-600">
                      Your property affairs remain private under PMA protection
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {contractSigned && (
          <div className="text-center space-y-4">
            <Button
              onClick={() => {
                if (applicationData?.membershipLevel === 'Basic') {
                  window.location.href = '/basic-portal';
                } else {
                  onContinue();
                }
              }}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              {applicationData?.membershipLevel === 'Basic' 
                ? 'Access Your Member Resources' 
                : 'Continue to Member Portal'}
            </Button>
            
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Your membership is now active! Questions? Contact our member support team at{' '}
              <a href="mailto:support@legacypropertycouncil.com" className="text-blue-600 hover:underline">
                support@legacypropertycouncil.com
              </a>
            </p>
          </div>
        )}

        {!contractSigned && (
          <div className="text-center">
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Please review and sign the PMA Agreement above to complete your membership activation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationSuccess;